"use client"

// useFamilyEconomicsData — Phase 4d.
//
// Read-only hook for family-grouped Product Economics. Separate from
// useConfigEconomicsData because the product-mode hook carries state machinery
// (custom/all caches, debounced productIds, presets) that doesn't apply when
// aggregation key is family_key. Family mode is always single-fetch /
// all-period — no picker, no presets.

import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import { buildEconomicsUrl } from "./build-economics-url"
import { ResponseCache, useResponseCacheGeneration } from "./_response-cache"
import type {
  ConfigEconomicsTotals,
  FamilyEconomicsItem,
  ProductEconomicsResponse,
} from "./types"

// Module-level cache (5-min TTL, auth-scoped key) — mirrors useBundleMatrixData
// / useStandaloneDecompose. Family mode is rendered by a component that unmounts
// when the user leaves the mode, so without this every re-entry refetches from
// the network; the cache makes a repeat visit within the window instant.
interface FamilyEconomicsPayload {
  families: FamilyEconomicsItem[]
  totals: ConfigEconomicsTotals | null
}
const CACHE_TTL_MS = 5 * 60 * 1000
const cache = new ResponseCache<FamilyEconomicsPayload>(CACHE_TTL_MS)

function cacheKey(opts: {
  userId: string
  activeTenantId: string
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal: number
  freeShippingThreshold: number
}): string {
  return [
    opts.userId,
    opts.activeTenantId,
    opts.integrationId,
    opts.monthStartUtc,
    opts.monthEndUtc,
    opts.minimumOrderTotal,
    opts.freeShippingThreshold,
  ].join("|")
}

interface UseFamilyEconomicsDataOptions {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal?: number
  freeShippingThreshold?: number
  /** Disable fetching (e.g. when toggle is in product mode). */
  enabled?: boolean
}

interface UseFamilyEconomicsDataResult {
  families: FamilyEconomicsItem[]
  totals: ConfigEconomicsTotals | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useFamilyEconomicsData({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal = 0,
  freeShippingThreshold = 0,
  enabled = true,
}: UseFamilyEconomicsDataOptions): UseFamilyEconomicsDataResult {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const cacheGeneration = useResponseCacheGeneration()
  const fetchRef = useRef(fetchWithAuth)
  fetchRef.current = fetchWithAuth
  const userId = user?.user_id ?? ""
  const activeTenantId = user?.impersonated_tenant_id ?? user?.tenant_id ?? ""

  const [families, setFamilies] = useState<FamilyEconomicsItem[]>([])
  const [totals, setTotals] = useState<ConfigEconomicsTotals | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Stale-response guard — same pattern as useBundlePerformance / family-mode
  // hooks. Prevents a slow in-flight response from overwriting a later one
  // (rapid period switch, integration change).
  const requestSeqRef = useRef(0)

  useEffect(() => {
    if (!isAuthenticated) cache.clear()
  }, [isAuthenticated])

  const load = useCallback(
    async (useCache: boolean) => {
      const localSeq = ++requestSeqRef.current
      if (
        !enabled ||
        !integrationId ||
        !isAuthenticated ||
        !monthStartUtc ||
        !monthEndUtc ||
        !userId ||
        !activeTenantId
      ) {
        if (localSeq === requestSeqRef.current) {
          setFamilies([])
          setTotals(null)
          setError(null)
          setIsLoading(false)
        }
        return
      }

      const key = cacheKey({
        userId,
        activeTenantId,
        integrationId,
        monthStartUtc,
        monthEndUtc,
        minimumOrderTotal,
        freeShippingThreshold,
      })

      if (useCache) {
        const cached = cache.get(key)
        if (cached != null) {
          if (localSeq === requestSeqRef.current) {
            setFamilies(cached.families)
            setTotals(cached.totals)
            setError(null)
            setIsLoading(false)
          }
          return
        }
      }

      setIsLoading(true)
      setError(null)
      try {
        const url = buildEconomicsUrl({
          integrationId,
          monthStartUtc,
          monthEndUtc,
          productIds: undefined,
          minimumOrderTotal,
          freeShippingThreshold,
          groupBy: "family",
        })
        const res = await fetchRef.current(url)
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        const data = (await res.json()) as ProductEconomicsResponse
        const payload: FamilyEconomicsPayload = {
          families: data.families ?? [],
          totals: data.totals,
        }
        cache.set(key, payload, cacheGeneration)
        if (localSeq === requestSeqRef.current) {
          setFamilies(payload.families)
          setTotals(payload.totals)
        }
      } catch (err: unknown) {
        if (localSeq === requestSeqRef.current) {
          const message = err instanceof Error ? err.message : "Failed to load economics data"
          setError(message)
          setFamilies([])
          setTotals(null)
        }
      } finally {
        if (localSeq === requestSeqRef.current) setIsLoading(false)
      }
    },
    [
      cacheGeneration,
      enabled,
      integrationId,
      isAuthenticated,
      userId,
      activeTenantId,
      monthStartUtc,
      monthEndUtc,
      minimumOrderTotal,
      freeShippingThreshold,
    ],
  )

  useEffect(() => {
    void load(true)
  }, [load])

  const refresh = useCallback(async () => {
    if (userId && activeTenantId && integrationId && monthStartUtc && monthEndUtc) {
      cache.delete(
        cacheKey({
          userId,
          activeTenantId,
          integrationId,
          monthStartUtc,
          monthEndUtc,
          minimumOrderTotal,
          freeShippingThreshold,
        }),
      )
    }
    await load(false)
  }, [
    load,
    userId,
    activeTenantId,
    integrationId,
    monthStartUtc,
    monthEndUtc,
    minimumOrderTotal,
    freeShippingThreshold,
  ])

  return { families, totals, isLoading, error, refresh }
}
